<template>
  <v-navigation-drawer
    v-model="internalOpen"
    temporary
    absolute
    location="end"
    right
    :width="internalOpen ? numericWidth : 0"
    class="app-side-panel"
    :scrim="scrim"
    @keydown.esc="closePanel"
    @click:outside="closePanel"
  >
    <div
      ref="contentWrap"
      class="app-side-panel__content"
      :style="{ width: contentWrapperStyle }"
      tabindex="-1"
    >
      <component
        v-if="component"
        :is="component"
        :key="componentKey"
        v-show="internalOpen"
        v-bind="mergedComponentProps"
        @close="closePanelFromChild"
      />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import type { PropType, DefineComponent, Component } from 'vue';

type PanelMode = 'fixed' | 'auto';

const DEFAULT_PANEL_WIDTH = 480;
const MIN_PANEL_WIDTH     = 320;

function normalizeWidth(
  val: number | string | null | undefined,
  fallback = DEFAULT_PANEL_WIDTH
): string {
  if (val == null) return `${fallback}px`;
  if (typeof val === 'number') return `${val}px`;
  const s = String(val).trim();
  if (/[a-z%]+$/i.test(s)) return s;
  const n = Number(s);
  return Number.isFinite(n) ? `${n}px` : `${fallback}px`;
}

export default {
  name: 'AppSidePanel',
  props: {
    modelValue: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    component: {
      type: [Object, Function, String] as PropType<
        Component | DefineComponent<any, any, any> | null
      >,
      default: null
    },
    componentProps: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({})
    },
    mode: {
      type: String as PropType<PanelMode>,
      default: 'fixed'
    },
    fixedWidth: {
      type: [Number, String] as PropType<number | string | undefined>,
      default: DEFAULT_PANEL_WIDTH
    },
    maxWidth: {
      type: [Number, String] as PropType<number | string | undefined>,
      default: 900
    },
    scrim: {
      type: [Boolean, String] as PropType<boolean | string>,
      default: true
    }
  },
  emits: ['update:modelValue', 'closed'],
  data() {
    const fw = normalizeWidth(this.fixedWidth)
    return {
      internalOpen: Boolean(this.modelValue),
      computedWidth: fw,
      resizeObserver: null as ResizeObserver | null
    }
  },
  computed: {
    numericWidth(): number {
      const s = String(this.computedWidth || `${DEFAULT_PANEL_WIDTH}px`).trim()
      const num = Number(s.replace(/px$/i, ''))
      return Number.isFinite(num) && num > 0
        ? Math.round(num)
        : DEFAULT_PANEL_WIDTH
    },
    contentWrapperStyle(): string {
      return this.computedWidth
    },
    componentKey(): string | number | null {
      if (!this.component) return null
      return (
        (this.component as any).name ||
        (this.component as any).__name ||
        String(this.component)
      )
    },
    mergedComponentProps(): Record<string, unknown> {
      return {
        ...(this.componentProps || {}),
        open: this.internalOpen
      }
    }
  },
  watch: {
    modelValue(val: boolean) {
      this.internalOpen = val
    },
    internalOpen(val: boolean) {
      this.$emit('update:modelValue', val)
      if (!val) {
        this.emitClosed()
      }
    },
    component() {
      this.$nextTick(() => {
        if (this.mode === 'auto') this.measureWidth()
      })
    },
    mode(val: PanelMode) {
      this.$nextTick(() => {
        if (val === 'auto') this.measureWidth()
        else this.setFixedWidth()
      })
    },
    fixedWidth() {
      if (this.mode !== 'auto') this.setFixedWidth()
    }
  },
  mounted() {
    if (this.mode === 'auto') this.measureWidth()
    else this.setFixedWidth()

    if (
      this.mode === 'auto' &&
      typeof window !== 'undefined' &&
      typeof (window as any).ResizeObserver !== 'undefined'
    ) {
      this.resizeObserver = new (window as any)
        .ResizeObserver(() => {
          this.measureWidth()
        }) as ResizeObserver
      if (this.$refs.contentWrap && this.resizeObserver) {
        this.resizeObserver.observe(this.$refs.contentWrap as Element)
      }
    }
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.contentWrap) {
      try {
        this.resizeObserver.unobserve(this.$refs.contentWrap as Element)
      } catch {
        // TODO: Logging
      }
      this.resizeObserver = null
    }
  },
  methods: {
    setFixedWidth(): void {
      this.computedWidth = normalizeWidth(this.fixedWidth)
    },
    measureWidth(): void {
      const el = this.$refs.contentWrap as HTMLElement | undefined
      if (!el) {
        this.setFixedWidth()
        return
      }

      const prevWidth = el.style.width
      const prevWhite = el.style.whiteSpace

      el.style.width      = 'auto'
      el.style.whiteSpace = 'nowrap'

      const scrollW = el.scrollWidth || el.offsetWidth || 0
      const maxW    = Number(this.maxWidth == null ? 900 : this.maxWidth)
      const desired = Math.min(scrollW, isNaN(maxW) ? scrollW : maxW)

      el.style.width      = prevWidth
      el.style.whiteSpace = prevWhite

      const widthPx = Math.max(desired, MIN_PANEL_WIDTH)
      this.computedWidth = `${widthPx}px`
    },
    closePanel(): void {
      this.internalOpen = false
    },
    closePanelFromChild(): void {
      this.closePanel()
    },
    emitClosed(): void {
      this.$emit('closed')
    }
  }
}
</script>

<style scoped>
.app-side-panel { will-change: transform; }

:deep(.v-navigation-drawer):not(.v-navigation-drawer--open) {
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
}

.app-side-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  background: var(--v-theme-surface, #fff);
}

.app-side-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-side-panel__content {
  max-height: 100vh;
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
}

@media (max-width: 600px) {
  .app-side-panel__content { padding: 12px; }
}

html { scrollbar-gutter: stable; }
</style>
